/**
 * Live transcript timeline (implementation-plan.md §5.1 "Live transcript timeline").
 *
 * Words appear as the transcription proxy delivers them, already mapped onto the video
 * timeline by the sync engine. Tapping a line jumps the player to that moment.
 */
import { useEffect, useRef } from 'react';
import { formatClock } from './upload';
import type { TranscriptSegmentView } from '../session/store';

export interface TranscriptTimelineProps {
  segments: TranscriptSegmentView[];
  currentTimeS: number;
  onSeek: (seconds: number) => void;
}

export function TranscriptTimeline({ segments, currentTimeS, onSeek }: TranscriptTimelineProps) {
  const listRef = useRef<HTMLOListElement | null>(null);
  const activeRef = useRef<HTMLLIElement | null>(null);

  // Follow the playhead while recording; a manual scroll is left alone until the next line.
  useEffect(() => {
    if (!activeRef.current) return;
    activeRef.current.scrollIntoView({ block: 'nearest' });
  }, [segments.length, currentTimeS]);

  const activeIndex = segments.reduce(
    (best, segment, index) => (segment.startS <= currentTimeS ? index : best),
    -1,
  );

  return (
    <section className="rounded-lg border border-chalk-200 bg-white/70 p-5 shadow-sm">
      <h2 className="mb-3 flex items-center justify-between text-lg font-semibold tracking-tight">
        Transcript
        <span className="text-xs font-normal text-charcoal-500" data-testid="transcript-count">
          {segments.length} {segments.length === 1 ? 'line' : 'lines'}
        </span>
      </h2>

      {segments.length === 0 ? (
        <p data-testid="transcript-empty" className="text-sm text-charcoal-500">
          Nothing said yet. Start the session and talk over the footage — lines appear here as they
          are transcribed.
        </p>
      ) : (
        <ol
          ref={listRef}
          data-testid="transcript-list"
          className="max-h-72 space-y-1 overflow-y-auto pr-1 text-sm"
        >
          {segments.map((segment, index) => (
            <li
              key={`${segment.startS}-${index}`}
              ref={index === activeIndex ? activeRef : null}
              data-testid="transcript-segment"
              data-start-s={segment.startS.toFixed(3)}
              data-end-s={segment.endS.toFixed(3)}
              className={`flex cursor-pointer gap-3 rounded px-2 py-1.5 transition-colors ${
                index === activeIndex ? 'bg-copper-300/30' : 'hover:bg-chalk-200/60'
              }`}
              onClick={() => onSeek(segment.startS)}
            >
              <span className="w-12 shrink-0 font-mono text-xs text-charcoal-500">
                {formatClock(segment.startS)}
              </span>
              <span data-testid="transcript-text">{segment.text}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
